import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { MapPin, Plus, Edit2, Trash2, Home, Building } from 'lucide-react';
import { toast } from 'sonner';

import AddressesAPI from '../utils/addressesAPI';
import LocationsAPI from '../utils/locationsAPI';
import LocationPickerMap from '../components/LocationPickerMap';

export default function AddressesPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editingAddressId, setEditingAddressId] = useState(null);

  const prevCityIdRef = useRef(null);
  const prevDistrictIdRef = useRef(null);

  const [cities, setCities] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [neighborhoods, setNeighborhoods] = useState([]);

  const [form, setForm] = useState({
    title: 'Ev',
    name: '',
    phone: '',
    city_id: '',
    city_name: '',
    district_id: '',
    district_name: '',
    neighborhood_id: '',
    neighborhood_name: '',
    address_line: '',
    latitude: null,
    longitude: null,
  });

  useEffect(() => {
    const load = async () => {
      if (!isAuthenticated) {
        setAddresses([]);
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const res = await AddressesAPI.getAddresses();
        setAddresses(res.addresses || []);
      } catch (e) {
        if (e.message !== 'NOT_AUTHENTICATED') toast.error('Adresler yüklenemedi');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [isAuthenticated]);

  useEffect(() => {
    const loadCities = async () => {
      if (!isAuthenticated) return;
      try {
        const res = await LocationsAPI.getCities();
        setCities(res.cities || []);
      } catch {}
    };
    loadCities();
  }, [isAuthenticated]);

  useEffect(() => {
    const loadSuggested = async () => {
      if (!isAuthenticated) return;
      if (!showCreate) return;
      const hasLat = typeof form.latitude === 'number' && Number.isFinite(form.latitude);
      const hasLng = typeof form.longitude === 'number' && Number.isFinite(form.longitude);
      if (hasLat && hasLng) return;

      try {
        if (navigator.geolocation) {
          const pos = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, {
              enableHighAccuracy: true,
              timeout: 12000,
              maximumAge: 30000,
            });
          });
          const lat = Number(pos?.coords?.latitude);
          const lng = Number(pos?.coords?.longitude);
          if (Number.isFinite(lat) && Number.isFinite(lng)) {
            setForm((p) => {
              const curHasLat = typeof p.latitude === 'number' && Number.isFinite(p.latitude);
              const curHasLng = typeof p.longitude === 'number' && Number.isFinite(p.longitude);
              if (curHasLat && curHasLng) return p;
              return { ...p, latitude: lat, longitude: lng };
            });
            return;
          }
        }
      } catch {
        // ignore
      }

      try {
        const res = await AddressesAPI.suggestLocation();
        const lat = Number(res?.latitude);
        const lng = Number(res?.longitude);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
        setForm((p) => {
          const curHasLat = typeof p.latitude === 'number' && Number.isFinite(p.latitude);
          const curHasLng = typeof p.longitude === 'number' && Number.isFinite(p.longitude);
          if (curHasLat && curHasLng) return p;
          return { ...p, latitude: lat, longitude: lng };
        });
      } catch {
        // ignore
      }
    };

    loadSuggested();
  }, [isAuthenticated, showCreate, form.latitude, form.longitude]);

  useEffect(() => {
    const loadDistricts = async () => {
      if (!form.city_id) {
        setDistricts([]);
        setNeighborhoods([]);
        return;
      }
      try {
        const res = await LocationsAPI.getDistricts(form.city_id);
        setDistricts(res.districts || []);
      } catch {
        setDistricts([]);
      }
      setNeighborhoods([]);

      const prevCity = prevCityIdRef.current;
      if (prevCity && String(prevCity) !== String(form.city_id)) {
        setForm((p) => ({
          ...p,
          district_id: '',
          district_name: '',
          neighborhood_id: '',
          neighborhood_name: '',
        }));
      }

      prevCityIdRef.current = form.city_id;
    };
    loadDistricts();
  }, [form.city_id]);

  useEffect(() => {
    const loadNeighborhoods = async () => {
      if (!form.district_id) {
        setNeighborhoods([]);
        return;
      }
      try {
        const res = await LocationsAPI.getNeighborhoods(form.district_id);
        setNeighborhoods(res.neighborhoods || []);
      } catch {
        setNeighborhoods([]);
      }

      const prevDistrict = prevDistrictIdRef.current;
      if (prevDistrict && String(prevDistrict) !== String(form.district_id)) {
        setForm((p) => ({
          ...p,
          neighborhood_id: '',
          neighborhood_name: '',
        }));
      }

      prevDistrictIdRef.current = form.district_id;
    };
    loadNeighborhoods();
  }, [form.district_id]);

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12">
        <div className="bg-white rounded-xl p-12 text-center max-w-md">
          <MapPin className="w-24 h-24 mx-auto text-gray-300 mb-6" />
          <h2 className="font-manrope font-bold text-2xl mb-4">Giriş Yapın</h2>
          <p className="text-gray-600 mb-8">Adreslerinizi görüntülemek için giriş yapmanız gerekiyor.</p>
          <Link
            to="/giris"
            className="inline-block bg-trendyol-orange hover:bg-orange-600 text-white font-semibold px-8 py-3 rounded-lg transition-colors"
          >
            Giriş Yap
          </Link>
        </div>
      </div>
    );
  }

  const handleDelete = async (id) => {
    try {
      await AddressesAPI.deleteAddress(id);
      const res = await AddressesAPI.getAddresses();
      setAddresses(res.addresses || []);
      toast.success('Adres silindi');
    } catch (e) {
      if (e.message === 'NOT_AUTHENTICATED') {
        toast.error('Lütfen giriş yapın');
        navigate('/giris');
      } else {
        toast.error(e.message || 'Adres silinemedi');
      }
    }
  };

  const handleSetDefault = async (id) => {
    try {
      await AddressesAPI.setDefaultAddress(id);
      const res = await AddressesAPI.getAddresses();
      setAddresses(res.addresses || []);
      toast.success('Varsayılan adres güncellendi');
    } catch (e) {
      toast.error(e.message || 'Varsayılan adres güncellenemedi');
    }
  };

  const handleCreate = async () => {
    if (
      !form.title ||
      !form.name ||
      !form.phone ||
      !form.city_id ||
      !form.district_id ||
      !form.neighborhood_id ||
      !form.address_line
    ) {
      toast.error('Lütfen tüm alanları doldurun');
      return;
    }

    try {
      if (editingAddressId) {
        await AddressesAPI.updateAddress(editingAddressId, {
          ...form,
          is_default: undefined,
        });
      } else {
        await AddressesAPI.createAddress({
          ...form,
          is_default: addresses.length === 0,
        });
      }

      const res = await AddressesAPI.getAddresses();
      setAddresses(res.addresses || []);
      setShowCreate(false);
      setEditingAddressId(null);
      toast.success(editingAddressId ? 'Adres güncellendi' : 'Adres kaydedildi');
    } catch (e) {
      if (e.message === 'NOT_AUTHENTICATED') {
        toast.error('Lütfen giriş yapın');
        navigate('/giris');
      } else {
        toast.error(e.message || 'Adres kaydedilemedi');
      }
    }
  };

  const handleEdit = (address) => {
    const a = address || {};
    setEditingAddressId(a.id);
    prevCityIdRef.current = a.city_id || '';
    prevDistrictIdRef.current = a.district_id || '';
    setForm({
      title: a.title || 'Ev',
      name: a.name || '',
      phone: a.phone || '',
      city_id: a.city_id || '',
      city_name: a.city || '',
      district_id: a.district_id || '',
      district_name: a.district || '',
      neighborhood_id: a.neighborhood_id || '',
      neighborhood_name: a.neighborhood || '',
      address_line: a.address || '',
      latitude: typeof a.latitude === 'number' ? a.latitude : null,
      longitude: typeof a.longitude === 'number' ? a.longitude : null,
    });
    setShowCreate(true);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 md:py-12" data-testid="addresses-page">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="font-manrope font-bold text-2xl md:text-3xl">Adreslerim</h1>
          <Link
            to="/hesabim"
            className="text-trendyol-orange hover:underline font-medium"
          >
            Hesabıma Dön
          </Link>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Add New Address Card */}
          <button
            className="border-2 border-dashed border-gray-300 rounded-xl p-8 hover:border-trendyol-orange transition-colors flex flex-col items-center justify-center gap-3 min-h-[280px]"
            data-testid="add-address-button"
            onClick={() => setShowCreate(true)}
          >
            <Plus className="w-12 h-12 text-gray-400" />
            <span className="font-semibold text-gray-600">Yeni Adres Ekle</span>
          </button>

          {showCreate && (
            <div className="bg-white rounded-xl p-6 border-2 border-gray-200" data-testid="create-address-form">
              <h3 className="font-semibold text-lg mb-4">{editingAddressId ? 'Adresi Düzenle' : 'Yeni Adres'}</h3>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium mb-1">Adres Başlığı</label>
                    <input
                      type="text"
                      value={form.title}
                      onChange={(e) => setForm({ ...form, title: e.target.value })}
                      className="w-full border-2 border-gray-200 rounded-lg px-3 py-2 focus:border-trendyol-orange focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Ad Soyad</label>
                    <input
                      type="text"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="w-full border-2 border-gray-200 rounded-lg px-3 py-2 focus:border-trendyol-orange focus:outline-none"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Telefon</label>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="w-full border-2 border-gray-200 rounded-lg px-3 py-2 focus:border-trendyol-orange focus:outline-none"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium mb-1">İl</label>
                    <select
                      value={form.city_id}
                      onChange={(e) => {
                        const id = e.target.value;
                        const city = cities.find((c) => String(c.id) === String(id));
                        setForm({ ...form, city_id: id, city_name: city?.name || '' });
                      }}
                      className="w-full border-2 border-gray-200 rounded-lg px-3 py-2 focus:border-trendyol-orange focus:outline-none"
                    >
                      <option value="">Seçiniz</option>
                      {cities.map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">İlçe</label>
                    <select
                      value={form.district_id}
                      onChange={(e) => {
                        const id = e.target.value;
                        const d = districts.find((x) => String(x.id) === String(id));
                        setForm({ ...form, district_id: id, district_name: d?.name || '' });
                      }}
                      className="w-full border-2 border-gray-200 rounded-lg px-3 py-2 focus:border-trendyol-orange focus:outline-none"
                      disabled={!form.city_id}
                    >
                      <option value="">Seçiniz</option>
                      {districts.map((d) => (
                        <option key={d.id} value={d.id}>{d.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Mahalle</label>
                  <select
                    value={form.neighborhood_id}
                    onChange={(e) => {
                      const id = e.target.value;
                      const n = neighborhoods.find((x) => String(x.id) === String(id));
                      setForm({ ...form, neighborhood_id: id, neighborhood_name: n?.name || '' });
                    }}
                    className="w-full border-2 border-gray-200 rounded-lg px-3 py-2 focus:border-trendyol-orange focus:outline-none"
                    disabled={!form.district_id}
                  >
                    <option value="">Seçiniz</option>
                    {neighborhoods.map((n) => (
                      <option key={n.id} value={n.id}>{n.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Adres</label>
                  <textarea
                    value={form.address_line}
                    onChange={(e) => setForm({ ...form, address_line: e.target.value })}
                    className="w-full border-2 border-gray-200 rounded-lg px-3 py-2 focus:border-trendyol-orange focus:outline-none"
                    rows="3"
                  />
                </div>
                <div>
                  <LocationPickerMap
                    label="Konum (Haritadan Seç)"
                    value={{
                      lat: typeof form.latitude === 'number' ? form.latitude : null,
                      lng: typeof form.longitude === 'number' ? form.longitude : null,
                    }}
                    onChange={({ lat, lng }) => setForm({ ...form, latitude: lat, longitude: lng })}
                  />
                  <div className="grid grid-cols-2 gap-3 mt-3">
                    <div>
                      <label className="block text-sm font-medium mb-1">Enlem (lat)</label>
                      <input
                        type="number"
                        value={form.latitude ?? ''}
                        onChange={(e) => setForm({ ...form, latitude: e.target.value === '' ? null : Number(e.target.value) })}
                        className="w-full border-2 border-gray-200 rounded-lg px-3 py-2 focus:border-trendyol-orange focus:outline-none"
                        step="any"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Boylam (lng)</label>
                      <input
                        type="number"
                        value={form.longitude ?? ''}
                        onChange={(e) => setForm({ ...form, longitude: e.target.value === '' ? null : Number(e.target.value) })}
                        className="w-full border-2 border-gray-200 rounded-lg px-3 py-2 focus:border-trendyol-orange focus:outline-none"
                        step="any"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-gray-600 mt-2">Haritaya tıklayarak pin bırakabilirsin. Koordinat girmezsen adres yine kaydedilir.</p>
                </div>
                <div className="flex gap-2 justify-end">
                  <button
                    onClick={() => {
                      setShowCreate(false);
                      setEditingAddressId(null);
                    }}
                    className="px-4 py-2 border-2 border-gray-300 rounded-lg hover:border-gray-400"
                  >
                    Vazgeç
                  </button>
                  <button
                    onClick={handleCreate}
                    className="px-4 py-2 bg-trendyol-orange hover:bg-orange-600 text-white font-semibold rounded-lg"
                  >
                    {editingAddressId ? 'Güncelle' : 'Kaydet'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Address Cards */}
          {loading ? (
            <div className="bg-white rounded-xl p-12 text-center">
              <p className="text-gray-600">Yüklenıyor...</p>
            </div>
          ) : addresses.map((address) => (
            <div
              key={address.id}
              className="bg-white rounded-xl p-6 border-2 border-gray-200 hover:border-trendyol-orange transition-colors relative"
              data-testid={`address-${address.id}`}
            >
              {address.isDefault && (
                <div className="absolute top-4 right-4 bg-trendyol-orange text-white text-xs font-bold px-3 py-1 rounded-full">
                  VARSAYILAN
                </div>
              )}

              <div className="flex items-center gap-3 mb-4">
                {String(address.title || '').toLowerCase().includes('ev') ? (
                  <Home className="w-6 h-6 text-trendyol-orange" />
                ) : (
                  <Building className="w-6 h-6 text-trendyol-orange" />
                )}
                <h3 className="font-semibold text-lg">{address.title}</h3>
              </div>

              <div className="space-y-2 text-sm text-gray-700 mb-4">
                <p className="font-medium">{address.name}</p>
                <p>{address.phone}</p>
                <p>{address.address}</p>
                <p>
                  {address.district} / {address.city}
                </p>
                {typeof address.latitude === 'number' && typeof address.longitude === 'number' ? (
                  <p className="text-xs text-gray-500">Konum: {String(address.latitude)}, {String(address.longitude)}</p>
                ) : null}
              </div>

              <div className="flex gap-2 pt-4 border-t border-gray-200">
                <button
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 border-2 border-gray-300 rounded-lg hover:border-trendyol-orange transition-colors"
                  onClick={() => handleEdit(address)}
                  data-testid={`edit-address-${address.id}`}
                >
                  <Edit2 className="w-4 h-4" />
                  Düzenle
                </button>
                <button
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 border-2 border-red-300 text-red-600 rounded-lg hover:border-red-500 transition-colors"
                  onClick={() => handleDelete(address.id)}
                  data-testid={`delete-address-${address.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                  Sil
                </button>
              </div>

              {!address.isDefault && (
                <div className="pt-3">
                  <button
                    className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg hover:border-trendyol-orange transition-colors"
                    onClick={() => handleSetDefault(address.id)}
                    data-testid={`set-default-${address.id}`}
                  >
                    Varsayılan Yap
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}